import React, { useEffect, useState } from "react";
import PageContainer from "./layout/PageContainer.js";
import { getNetlifyFunctionsBase } from "../utils/netlifyFunctions.js";

function formatDate(isoString) {
  if (!isoString) return "—";
  try {
    const d = new Date(isoString);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch (_) {
    return isoString;
  }
}

function subscriptionTypeLabel(interval) {
  if (!interval) return "One-time";
  const i = String(interval).toLowerCase();
  if (i === "monthly" || i === "month") return "Monthly subscription";
  if (i === "yearly" || i === "year") return "Yearly subscription";
  return "One-time";
}

const Billing = ({ verifiedEmail }) => {
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchInvoices = async () => {
      if (!verifiedEmail) return;
      setIsLoading(true);
      setError("");
      const base = getNetlifyFunctionsBase();
      const body = JSON.stringify({ email: verifiedEmail });
      const headers = { "Content-Type": "application/json" };

      try {
        // Pull latest invoices from Stripe into Supabase before reading the table.
        // Failures here must not block showing whatever is already stored.
        try {
          await fetch(`${base}/.netlify/functions/syncBillingInvoices`, {
            method: "POST",
            headers,
            body
          });
        } catch (syncErr) {
          console.error("[Billing] Stripe sync failed; loading stored invoices", syncErr);
        }

        const response = await fetch(`${base}/.netlify/functions/getBillingRecords`, {
          method: "POST",
          headers,
          body
        });
        if (!response.ok) {
          const errText = await response.text();
          throw new Error(errText || "Unable to load billing records.");
        }
        const data = await response.json();
        setInvoices(Array.isArray(data.invoices) ? data.invoices : []);
      } catch (err) {
        console.error(err);
        setError("Unable to load billing records right now.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoices();
  }, [verifiedEmail]);

  return (
    <PageContainer className="about-page billing-page">
      <h1 className="about-page__title">Billing</h1>

      <p className="about-page__lead">
        Invoices and next payment dates for your Ahavibes subscription.
      </p>

      <section className="about-section">
        <h2 className="about-section__heading">Subscription invoices</h2>

        {isLoading ? <p className="about-section__p">Loading invoices…</p> : null}
        {error ? <p className="login-error">{error}</p> : null}

        {!isLoading && !error && invoices.length === 0 ? (
          <p className="about-section__p">No invoices found yet.</p>
        ) : null}

        {!isLoading && !error && invoices.length > 0 ? (
          <div className="billing-table-wrap">
            <table className="billing-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Next payment date</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>{formatDate(invoice.created_at)}</td>
                    <td>{subscriptionTypeLabel(invoice.subscription_interval)}</td>
                    <td>{invoice.amount ? `$${invoice.amount}` : "—"}</td>
                    <td>{invoice.subscription_interval ? formatDate(invoice.next_payment_date) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </PageContainer>
  );
};

export default Billing;
